<template>
  <div class="single-vehicle-filter-result-component">
    <div v-if="vehicle">
      <!-- Badge -->
      <div v-if="showLeadTime" class="leadtime w-100" style="margin-bottom:5px;font-weight:bold;font-size:11px;text-transform:uppercase;">
        <span>
          Estimated delivery:
          <template v-if="leadTime">
            <span class="text-bold">{{ leadTime }}</span>
          </template>
          <template v-else>
            Please call the team
          </template>
        </span>
      </div>

      <div class="d-flex flex-column" style="height: 100%;">
        <!-- Vehicle Thumbnail -->
        <div>
          <vehicle-image :derivative="vehicle" :showMain="true" :enableGallery="true"></vehicle-image>
        </div>

        <!-- Vehicle Make & Model -->
        <div class="vehicle-make-model">
          <h3 v-if="vehicle">{{ vehicle.manufacturerName }} {{ vehicle.rangeName }}</h3>
          <h4 v-if="vehicle">{{ vehicle.modelName.replace(vehicle.rangeName + ' ', '') }} {{ vehicle.derivativeName }}</h4>
          <template v-if="scheme === 'bch'">
            <h5>Driver Grade: {{ driverGrade }}</h5>
          </template>
        </div>

        <!-- Vehicle Spec -->
        <div class="vehicle-spec">
          <ul class="specs">
            <li v-if="vehicle.mpg">
              <span class="key">Illustrative MPG</span>
              <span class="value">{{ vehicle.mpg }}</span>
            </li>
            <li v-if="vehicle.cO2">
              <span class="key">C0<sub>2</sub></span>
              <span class="value">{{ vehicle.cO2 }}</span>
            </li>
            <li v-if="vehicle.p11DValue">
              <span class="key">P11d Value</span>
              <span class="value">{{ currency(vehicle.p11DValue) }}</span>
            </li>
            <li v-if="vehicle.fuelTypeDescription">
              <span class="key">Fuel Type</span>
              <span class="value">{{ vehicle.fuelTypeDescription }}</span>
            </li>
            <li v-if="vehicle.maxEVRange">
              <span class="key">Max Range</span>
              <span class="value">{{ Math.round(Number(vehicle.maxEVRange)) }} miles</span>
            </li>
            <li v-if="vehicle.engineSize">
              <span class="key">Engine Size</span>
              <span class="value">{{ vehicle.engineSize }}</span>
            </li>
            <li v-if="vehicle.enginePowerKW">
              <span class="key">Engine Power</span>
              <span class="value">{{ vehicle.enginePowerKW }}kW</span>
            </li>
            <li v-if="vehicle.transmission">
              <span class="key">Transmission</span>
              <span class="value">{{ vehicle.transmission }}</span>
            </li>
          </ul>
        </div>

        <!-- Vehicle Costs -->
        <div class="vehicle-costs mt-auto">
          <div class="w-100">

            <template v-if="scheme === 'salsac'">
              <span class="percentage d-block">
                {{ currentProduct.shortlisted_card_titles_and_text_payment_title }}
                <VDropdown>
                  <button class="tooltip-button"><i class="icon-info-solid"/></button>
                  <template #popper>
                    <div v-html="currentProduct.shortlisted_card_titles_and_text_payment_hotspot_text"></div>
                  </template>
                </VDropdown>
              </span>
              <span v-if="vehicle.p11DValue" class="price d-block">{{ currency(vehicle.p11DValue) }}</span>
            </template>

            <template v-else-if="scheme === 'bch'">
              <ul class="bch-shortlist-costs">
                <template v-if="ratebookEnabled">
                  <li v-if="RatebookHelper.averageMonthlyBik(ratebook, bodyParams.Term)">
                    <span class="cost">
                      <span class="label">Monthly BiK</span>
                      <span class="value">£{{ RatebookHelper.averageMonthlyBik(ratebook, bodyParams.Term) }}</span>
                    </span>
                  </li>
                  <li v-if="RatebookHelper.tradeUpDownRawValue(ratebook)">
                    <span class="cost">
                      <span class="label">
                        Trade
                        <template v-if="RatebookHelper.tradeUpDownRawValue(ratebook) < 0">Down</template>
                        <template v-else>Up</template>
                      </span>
                      <span class="value">£{{ RatebookHelper.tradeUpDown(ratebook, bodyParams.Term) }}</span>
                    </span>
                  </li>
                  <li v-if="RatebookHelper.oneOffContribution(ratebook, bodyParams.Term)">
                    <span class="cost">
                      <span class="label">One Off Contribution</span>
                      <span class="value">£{{ RatebookHelper.oneOffContribution(ratebook, bodyParams.Term) }}</span>
                    </span>
                  </li>
                </template>
                <template v-else>
                  <li v-if="configuration.showContribution !== 'monthly'">
                    <span class="cost">
                      <span class="label">Monthly BiK</span>
                      <span class="value">{{ currency(bchMonthlyBik) }}</span>
                    </span>
                  </li>
                  <li v-if="configuration.showRental === 'totaleffective'">
                    <span class="cost">
                      <span class="label">Vehicle Rental</span>
                      <span class="value">{{
                          currency(this.vehicle.quotes[0].details.lifeCosts.totalEffectiveRental)
                        }}</span>
                    </span>
                  </li>
                  <li v-if="configuration.showRental === 'rentalexvat'">
                    <span class="cost">
                      <span class="label">Vehicle Rental</span>
                      <span class="value">{{ currency(this.vehicle.quotes[0].details.pricing.totalExVAT) }}</span>
                    </span>
                  </li>
                </template>
              </ul>
              <template v-if="ratebookEnabled">
                <template v-if="showBchVehicleRental || showBchWlc">
                  <hr>
                  <ul class="bch-shortlist-costs">
                  <!--  As per Chris Paddick 12/6/25 they want to use the Jaama rental figure instead of Deloite, but this isn't available until
                    after it's added to a shortlist, so hide here but still show on subsequent pages-->
                  <!--<li v-if="showBchVehicleRental && RatebookHelper.vehicleRental(ratebook)">
                      <span class="cost">
                        <span class="label">Vehicle Rental</span>
                        <span class="value">£{{ RatebookHelper.vehicleRental(ratebook) }}</span>
                      </span>
                    </li>-->
                    <li v-if="showBchWlc && RatebookHelper.wholeLifeCost(ratebook, bodyParams.Term)">
                      <span class="cost">
                        <span class="label">Whole Life Cost</span>
                        <span class="value">£{{ RatebookHelper.wholeLifeCost(ratebook, bodyParams.Term) }}</span>
                      </span>
                    </li>
                  </ul>
                </template>

                <div class="bch-total">
                  <div class="text">
                    Estimated NET monthly Cost
                    <VDropdown>
                      <span class="tooltip-info white">?</span>
                      <template #popper>
                        <div class="popper-content">
                          <p>This cost is the average monthly BIK, plus the cost of any <br>
                            one-off contribution spread over the contract duration. As per <br>
                            the policy, the one-off contribution will need to be paid in full <br>
                            before the vehicle is delivered.</p>
                        </div>
                      </template>
                    </VDropdown>
                  </div>
                  <div class="cost">
                    <template v-if="RatebookHelper.netCostPounds(ratebook, bodyParams.Term)">£{{ RatebookHelper.netCostPounds(ratebook, bodyParams.Term) }}<span class="pence">.{{ RatebookHelper.netCostPence(ratebook, bodyParams.Term) }}</span></template>
                    <template v-else>n/a</template>
                  </div>
                </div>
              </template>
            </template>

            <template v-else-if="scheme === 'ecoamaps'">
              <span class="percentage d-block">
                {{ currentProduct.shortlisted_card_titles_and_text_payment_title }}
                <VDropdown>
                  <button class="tooltip-button"><i class="icon-info-solid"/></button>
                  <template #popper>
                    <div v-html="currentProduct.shortlisted_card_titles_and_text_payment_hotspot_text"></div>
                  </template>
                </VDropdown>
              </span>
              <span class="price d-block">{{ currency(monthlyPayment) }}</span>
            </template>

          </div>
        </div>

        <!-- Vehicle Shortlist -->
        <div class="vehicle-shortlist">

          <div v-if="isShortListed || vehicle.selected" class="d-flex justify-content-between">
            <span class="added">
              <span class="d-flex flex-row justify-content-between">
                <span>{{ currentProduct.shortlisted_card_titles_and_text_added_button_text }}</span> <i
                class="icon-tick"></i>
              </span>
            </span>
            <button @click="removeFromShortlist(vehicle)" class="button button--remove"><i
              class="icon-cross"></i></button>
          </div>
          <div v-else>
            <button @click="addToShortlist(vehicle)" class="button button--steelBlue-ghost w-100">{{
                currentProduct.shortlisted_card_titles_and_text_add_button_text
              }}
            </button>
          </div>
        </div>

        <!-- Standard Specification -->
        <div class="standard-specification">
          <p><a @click="showStandardSpecification(vehicle)" class="link">
            {{ currentProduct.shortlisted_card_titles_and_text_specification_link_text }}</a></p>
        </div>

        <!--<vehicle-image :derivative="vehicle" :showThumbs="true" :showMain="false" :enableGallery="true"/>-->
      </div>

    </div>
    <div v-else>
      <loading-icon></loading-icon>
    </div>
  </div>

</template>

<script>
import LoadingIcon from "../loading-icon";
import {EventBus} from '@/event-bus.js';
import VehicleImage from "@/components/vehicle-details/vehicle-image";
import VehicleCosts from '../../helpers/vehicle-costs';
import {fuelTypeElectric, fuelTypeElectricOrPluginHybrid} from "@/helpers/fuel-type";
import {currency} from '@/helpers/filters';
import RatebookHelper from '@/helpers/ratebook';
import {useRoute} from "vue-router";

export default {
  components: {VehicleImage, LoadingIcon},
  props: [
    'vehicle',
    'shortListItems',
    'shortlist',
    'session',
    'filterParams',
    'bodyParams',
    'currentProduct',
    'scheme',
    'adminPortalData',
    'configuration',
    'driverGradeId',
    'currentDriverGrade',
    'driverGradesById',
  ],
  data() {
    return {
      tooltip: true
    }
  },

  setup() {
    const route = useRoute()
    return {route}
  },

  created() {
    this.getVehicleBikByRate = VehicleCosts.getVehicleBikByRate;
  },

  computed: {
    RatebookHelper() {
      return RatebookHelper
    },
    // A basic, non-definitive way of checking if a user has a ratebook for this vehicle, and thus should see the ratebook values
    ratebookEnabled() {
      return this?.RatebookHelper.isRatebookEnabledGrade(this.vehicle.quotes[0].details.driverGradeName);
    },
    ratebook() {
      return this.vehicle?.deloitteCalculationResult;
    },
    isShortListed() {
      if (!this.shortListItems[this.vehicle.derivativeID] || this.shortListItems[this.vehicle.derivativeID].length < 1) {
        return false;
      }
      const shortListedItems = this.shortListItems[this.vehicle.derivativeID].filter((item) => {
        return item.driverGradeId === this.currentDriverGrade.driverGradeID
      })

      return shortListedItems.length > 0;
    },

    monthlyPayment() {
      // https://www.wrike.com/open.htm?id=707127986
      //TODO the £3.60 addition will definitely need removing soon
      return Number(this.vehicle.quotes[0].details.pricing.totalIncVAT) + 3.6;
    },
    bchMonthlyBik() {
      return Number(this.vehicle?.ratebook?.bikTaxAverage || this.getVehicleBikByRate(this.vehicle, this.session.driverProfile.incomeTaxBand.ratePercent)).toFixed(2);
    },
    showLeadTime() {
      return this.adminPortalData.content?.products?.[this.route.params.scheme]?.[0]?.your_shortlist_show_lead_times
    },
    leadTime() {
      return this.adminPortalData.content?.vehicles?.leadTimes?.[this.vehicle.manufacturerName.toLowerCase()]?.[this.vehicle.modelName.toLowerCase()]?.lead_time;
    },
    driverGrade() {
      return this.driverGradesById?.[this.vehicle.quotes[0].definition.driverGradeID].driverGradeDefinition.friendlyName ?? this.vehicle.quotes[0].details.driverGradeName;
    },
    currentProduct() {
      return this.adminPortalData.content?.products?.[this.route.params.scheme]?.[0];
    },
    showBchWlc() {
      return RatebookHelper.showWholeLifeCosts(this.currentProduct);
    },
    showBchVehicleRental() {
      return RatebookHelper.showVehicleRental(this.currentProduct);
    },
  },
  methods: {
    fuelTypeElectric,
    fuelTypeElectricOrPluginHybrid,
    currency,

    addToShortlist(vehicle) {

      EventBus.$emit('loadingTitle', 'Adding Vehicle to Shortlist');
      EventBus.$emit('loading', true);

      const carHireType = this.session.driverProfile['BCH'] ? 'BCH' : 'PCH';

      this.$http.post('/api/quotation', {
        method: 'POST',
        endpoint: 'quotecalculator/shortlists/' + this.shortlist.shortListID,
        refresh_ratebook: this.ratebookEnabled,
        shortlistId: this.shortlist.shortListID,
        scheme: this.scheme,
        body_params: {
          QuoteName: this.route.params.scheme + '--' + vehicle.derivativeID + '-' + (new Date().toISOString()),
          DerivativeID: vehicle.derivativeID,
          CustomerID: this.session.driverProfile.customerID,
          DriverID: this.session.driverProfile.driverID,
          DriverGradeID: this.currentDriverGrade.driverGradeID,
          AssetTypeID: this.currentDriverGrade.assetTypes[0].assetTypeID,
          Plan: {
            productID: this.currentDriverGrade.products[0].productID,
            paymentPlanID: this.currentDriverGrade.defaultPaymentPlanId ?? this.currentDriverGrade.paymentPlans[0].paymentPlanID,
            term: this.bodyParams.Term,
            annualDistance: this.bodyParams.AnnualDistance,
            deposit: 0,
            qualifyingFlag: (this.scheme === 'ecoamaps' ? 2 : 1), // EcoAMAPS 2, BCH & Salac = 1 - getting this wrong affects the residual value calculations as per JCTSLA-12
          },
          FactoryOptions: [],
          ProductChargeTypes: [],
        },
      })
        .then(response => {
          EventBus.$emit('loading', false);

          if (response.data.status == 200) {

            vehicle.selected = true;

            this.$notify({
              group: 'notify',
              type: 'success',
              title: vehicle.manufacturerName + ' ' + vehicle.modelName,
              text: 'Has been added to your comparison shortlist'
            })

            // Instant feedback here but still emit the event.
            let shortListItems = this.shortListItems;
            Vue.nextTick(() => {
              shortListItems[response.data.data[0].definition.derivativeID].push({quote: response.data.data[0].quoteID, driverGradeId: this.currentDriverGrade.driverGradeID});
            })

            if (!this.shortListItems[response.data.data[0].definition.derivativeID]) {
              // If it doesn't exist, create an empty array for that key
              this.$set(this.shortListItems, response.data.data[0].definition.derivativeID, []);
            }

            // Push the new object into the array
            // this.shortListItems[response.data.data[0].definition.derivativeID].push({
            //   quote: response.data.data[0].quoteID,
            //   driverGradeId: currentDriverGrade
            // });
            this.$parent.shortListItemCount++;
            EventBus.$emit('shortlistUpdated', this.$parent.shortListItemCount);
            EventBus.$emit('addToShortlist', vehicle);

            this.$forceUpdate();

          } else {
            if (response.data.level === 'high') {
              EventBus.$emit('showError', true);
              EventBus.$emit('errorTitle', 'Cannot add to shortlist');
              EventBus.$emit('errorMessage', response.data.error.message);
            } else {
              this.$notify({
                group: 'notify',
                type: 'error',
                title: 'Error',
                text: response.data.error.message
              })

              for (let property in response.data.error.errors) {
                if (response.data.error.errors.hasOwnProperty(property)) {
                  response.data.error.errors[property].forEach((error) => {
                    this.$notify({
                      group: 'notify',
                      type: 'error',
                      title: 'Error',
                      text: error
                    })
                  });
                }
              }
            }
          }
        })
        .catch(err => {

          EventBus.$emit('loading', false);

          this.$notify({
            group: 'notify',
            type: 'error',
            title: 'Error',
            text: err.toString()
          })
          this.error = err.toString()
        });
    },

    removeFromShortlist(vehicle) {
      // Quick, out of scope workaround for the addToShortlist logic adding two items, one with both values undefined
      //  This then causes the flawed logic below (that always removes item 0 from the array) to fail because there's no quote set
      this.shortListItems[vehicle.derivativeID].forEach((item, index) => {
        if (item.quote === undefined) {
          this.shortListItems[vehicle.derivativeID].splice(index, 1);
        }
      })

      let quoteID = this.shortListItems[vehicle.derivativeID][0].quote;

      EventBus.$emit('loadingTitle', 'Removing Vehicle from Shortlist');
      EventBus.$emit('loading', true);

      this.$http.post('/api/quotation', {
        method: 'DELETE',
        endpoint: 'shortlists/' + this.shortlist.shortListID + '/quotes/' + quoteID,
      })
        .then(response => {

          EventBus.$emit('loading', false);

          if (response.data.status == 200) {

            vehicle.selected = false;

            this.$notify({
              group: 'notify',
              type: 'success',
              title: vehicle.manufacturerName + ' ' + vehicle.modelName,
              text: 'Has been removed from your comparison shortlist'
            })

            this.shortListItems[vehicle.derivativeID] = 0;
            this.$parent.shortListItemCount--;
            EventBus.$emit('shortlistUpdated', this.$parent.shortListItemCount);
            EventBus.$emit('removeFromShortlist', vehicle.derivativeID);

            this.$forceUpdate();

          } else {

            this.$notify({
              group: 'notify',
              type: 'error',
              title: 'Error',
              text: response.data.message
            })

            for (let property in response.data.errors) {
              if (response.data.errors.hasOwnProperty(property)) {
                response.data.errors[property].forEach((error) => {
                  this.$notify({
                    group: 'notify',
                    type: 'error',
                    title: 'Error',
                    text: error
                  })
                });
              }
            }

          }
        })
        .catch(err => {
          EventBus.$emit('loading', false);
          this.$notify({
            group: 'notify',
            type: 'error',
            title: 'Error',
            text: err.toString()
          })
          this.error = err.toString()
        });
    },

    showStandardSpecification(vehicle) {

      this.$parent.selectedVehicle = {vehicle: vehicle};

      this.$http.post('/api/quotation', {
        method: 'GET',
        endpoint: 'derivatives/' + vehicle.derivativeID,
      })
        .then(response => {
          if (response.data.status == 200) {

            let items = {};

            response.data.data.standardEquipmentItems.forEach((item) => {
              if (typeof items[item.standardEquipmentGroupName] === 'undefined') {
                items[item.standardEquipmentGroupName] = [];
              }
              items[item.standardEquipmentGroupName].push(item.standardEquipmentName);
            });

            this.$set(this.$parent.selectedVehicle, 'derivateInformation', items);


          } else {

            this.$notify({
              group: 'notify',
              type: 'error',
              title: 'Error',
              text: response.data.message
            })

            for (let property in response.data.errors) {
              if (response.data.errors.hasOwnProperty(property)) {
                response.data.errors[property].forEach((error) => {
                  this.$notify({
                    group: 'notify',
                    type: 'error',
                    title: 'Error',
                    text: error
                  })
                });
              }
            }

          }
        })
        .catch(err => {
          this.$notify({
            group: 'notify',
            type: 'error',
            title: 'Error',
            text: err.toString()
          })
          this.error = err.toString()
        });
    },

  }
};
</script>

<style scoped lang="scss">

</style>
